import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import HeroSection from '../components/HeroSection';
import CarrierCarousel from '../components/CarrierCarousel';
import ContactForm from '../components/ContactForm';
import SEO from '../components/SEO';

const Home = () => {
  const testimonials = [
    {
      name: 'Sarah Johnson',
      text: 'LifeLine Health Plans made my Medicare transition so easy! Paulette was incredibly knowledgeable and patient with all my questions.',
      date: '2 weeks ago',
      rating: 5
    },
    {
      name: 'Michael Chen',
      text: 'Best insurance experience I\'ve ever had. They truly work for you, not the insurance companies. Highly recommend!',
      date: '1 month ago',
      rating: 5
    },
    {
      name: 'Linda Martinez',
      text: 'Year-round support is amazing. I had a question about my coverage and got a response the same day. No waiting on hold with 1-800 numbers!',
      date: '3 weeks ago',
      rating: 5
    },
    {
      name: 'Robert Williams',
      text: 'Found me a better plan and saved me money during annual enrollment. They really know their stuff!',
      date: '1 week ago',
      rating: 5
    }
  ];

  return (
    <div className="home-page">
      <SEO 
        title="Home"
        description="LifeLine Health Plans - Your trusted Medicare insurance specialist in Michigan. Expert guidance for Medicare, individual & family health insurance, dental, and group coverage. Year-round support for seniors."
        keywords="Medicare insurance, health insurance Michigan, Medicare specialist, senior health coverage, Medicare plans, individual health insurance, family health insurance, dental insurance, group insurance"
        path="/"
      />
      {/* Hero Section Component */}
      <HeroSection />

      {/* What Our Clients Love Most Section - Light Gray Background */}
      <section className="py-8 px-4 bg-gray-100">
        <div className="container mx-auto max-w-6xl">
          <h2 className="text-3xl font-bold text-center mb-6 text-gray-900">WHAT OUR CLIENTS LOVE MOST</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <Card className="border-teal-200 hover:shadow-lg transition-shadow">
              <CardContent className="p-6 text-center">
                <h3 className="text-xl font-semibold mb-2 text-gray-900">We work for You not the insurance company</h3>
                <p className="text-gray-600 text-base">Independent agents focused on your needs.</p>
              </CardContent>
            </Card>
            <Card className="border-teal-200 hover:shadow-lg transition-shadow">
              <CardContent className="p-6 text-center">
                <h3 className="text-xl font-semibold mb-2 text-gray-900">Year Round Help</h3>
                <p className="text-gray-600 text-base">We are not a call center, we are here for you.</p>
              </CardContent>
            </Card>
            <Card className="border-teal-200 hover:shadow-lg transition-shadow">
              <CardContent className="p-6 text-center">
                <h3 className="text-xl font-semibold mb-2 text-gray-900">Unbiased Advice</h3>
                <p className="text-gray-600 text-base">We compare Multiple carriers, so get options that fit you.</p>
              </CardContent>
            </Card>
            <Card className="border-teal-200 hover:shadow-lg transition-shadow">
              <CardContent className="p-6 text-center">
                <h3 className="text-xl font-semibold mb-2 text-gray-900">Local Support</h3>
                <p className="text-gray-600 text-base">We are Michigan based. We focus on our community.</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Navy Blue "NO COST" Banner - Full Width */}
      <section className="w-full bg-[#1e3a8a] py-8">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl md:text-5xl font-bold text-center text-white">
            Our Services are NO COST
          </h2>
        </div>
      </section>

      {/* Three Steps to the Right Plan Section - Light Teal Background */}
      <section className="py-12 px-4 bg-teal-50">
        <div className="container mx-auto max-w-6xl">
          <h2 className="text-4xl font-bold text-center mb-12 text-gray-900">Three steps to the right plan.</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
            {/* Step 1 */}
            <div className="bg-white rounded-lg p-8 shadow-md">
              <div className="text-6xl font-bold text-teal-600 mb-4">01</div>
              <h3 className="text-2xl font-bold mb-4 text-gray-900">Share your priorities</h3>
              <p className="text-gray-700 text-lg mb-6">Tell us about your doctors, medications, and what matters most to you.</p>
              <div className="space-y-3">
                <Button asChild className="w-full bg-teal-600 hover:bg-teal-700 text-white text-lg py-6">
                  <a href="https://app.retireflo.com/paulette-lifeline-plans" target="_blank" rel="noopener noreferrer">
                    Take the Medicare Survey
                  </a>
                </Button>
                <Button asChild className="w-full bg-teal-600 hover:bg-teal-700 text-white text-lg py-6">
                  <a href="https://app.retireflo.com/paulette-%7C-insurance-for-individuals-&-families" target="_blank" rel="noopener noreferrer">
                    Take the Individual/Family Survey
                  </a>
                </Button>
              </div>
            </div>

            {/* Step 2 */}
            <div className="bg-white rounded-lg p-8 shadow-md">
              <div className="text-6xl font-bold text-teal-600 mb-4">02</div>
              <h3 className="text-2xl font-bold mb-4 text-gray-900">We compare plans</h3>
              <p className="text-gray-700 text-lg">We analyze options across carriers and explain the tradeoffs clearly.</p>
            </div>

            {/* Step 3 */}
            <div className="bg-white rounded-lg p-8 shadow-md">
              <div className="text-6xl font-bold text-teal-600 mb-4">03</div>
              <h3 className="text-2xl font-bold mb-4 text-gray-900">Enroll with confidence</h3>
              <p className="text-gray-700 text-lg mb-6">You choose the plan that fits—and we stay by your side year-round.</p>
              <Button asChild className="w-full bg-teal-600 hover:bg-teal-700 text-white text-lg py-6">
                <a href="https://calendly.com/llplans/30min" target="_blank" rel="noopener noreferrer">
                  Schedule a Meeting
                </a>
              </Button>
            </div>
          </div>

          <div className="text-center">
            <Button asChild size="lg" className="bg-teal-600 hover:bg-teal-700 text-white text-2xl px-12 py-8">
              <a href="https://app.retireflo.com/paulette-lifeline-plans" target="_blank" rel="noopener noreferrer">
                Start Your Free Review
              </a>
            </Button>
          </div>
        </div>
      </section>

      {/* Ways We Help Our Clients Section - White Background */}
      <section className="py-10 px-4 bg-white">
        <div className="container mx-auto max-w-7xl">
          <h2 className="text-3xl font-bold text-center mb-3 text-gray-900">Ways We Help Our Clients</h2>
          <p className="text-center text-gray-600 mb-8 text-lg">Our Services</p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <Link to="/medicare-explained" className="group">
              <Card className="overflow-hidden hover:shadow-xl transition-all transform hover:-translate-y-1 h-full">
                <div className="h-48 bg-white flex items-center justify-center p-4 overflow-hidden">
                  <img 
                    src="/images/fraud-2.png"
                    alt="Medicare Card"
                    className="w-full h-full object-contain"
                  />
                </div>
                <CardContent className="p-6">
                  <h3 className="text-xl font-bold mb-2 text-gray-900 group-hover:text-teal-600 transition-colors">Medicare Explained</h3>
                  <p className="text-gray-600">Learn everything about Medicare Parts A, B, C & D.</p>
                </CardContent>
              </Card>
            </Link>

            <Link to="/turning-65" className="group">
              <Card className="overflow-hidden hover:shadow-xl transition-all transform hover:-translate-y-1 h-full">
                <img 
                  src="/images/turning-65.png" 
                  alt="Turning 65" 
                  className="w-full h-48 object-cover"
                />
                <CardContent className="p-6">
                  <h3 className="text-xl font-bold mb-2 text-gray-900 group-hover:text-teal-600 transition-colors">Turning 65</h3>
                  <p className="text-gray-600">New to Medicare? We'll guide you every step.</p>
                </CardContent>
              </Card>
            </Link>

            <Link to="/already-have-medicare" className="group">
              <Card className="overflow-hidden hover:shadow-xl transition-all transform hover:-translate-y-1 h-full">
                <img 
                  src="/images/couple-laptop.jpg" 
                  alt="Couple with Medicare card" 
                  className="w-full h-48 object-cover"
                />
                <CardContent className="p-6">
                  <h3 className="text-xl font-bold mb-2 text-gray-900 group-hover:text-teal-600 transition-colors">Already Have Medicare</h3>
                  <p className="text-gray-600">Annual review to ensure your plan still fits your needs.</p>
                </CardContent>
              </Card>
            </Link>

            <Link to="/individual-family" className="group">
              <Card className="overflow-hidden hover:shadow-xl transition-all transform hover:-translate-y-1 h-full">
                <img 
                  src="/images/agent-helping-family.png" 
                  alt="Individual and Family" 
                  className="w-full h-48 object-cover"
                />
                <CardContent className="p-6">
                  <h3 className="text-xl font-bold mb-2 text-gray-900 group-hover:text-teal-600 transition-colors">Individual and Family</h3>
                  <p className="text-gray-600">Turning 26 | Self-Employed | Losing Coverage</p>
                </CardContent>
              </Card>
            </Link>

            <Link to="/dental" className="group">
              <Card className="overflow-hidden hover:shadow-xl transition-all transform hover:-translate-y-1 h-full">
                <img 
                  src="/images/dental-2.png" 
                  alt="Dental" 
                  className="w-full h-48 object-cover"
                  style={{objectPosition: 'center 20%'}}
                />
                <CardContent className="p-6">
                  <h3 className="text-xl font-bold mb-2 text-gray-900 group-hover:text-teal-600 transition-colors">Dental and Vision</h3>
                  <p className="text-gray-600">Affordable dental and vision coverage.</p>
                </CardContent>
              </Card>
            </Link>

            <Link to="/group-insurance" className="group">
              <Card className="overflow-hidden hover:shadow-xl transition-all transform hover:-translate-y-1 h-full">
                <img 
                  src="/images/small-business.jpg" 
                  alt="Small Business" 
                  className="w-full h-48 object-cover"
                />
                <CardContent className="p-6">
                  <h3 className="text-xl font-bold mb-2 text-gray-900 group-hover:text-teal-600 transition-colors">Small Business Health Insurance</h3>
                  <p className="text-gray-600">Group coverage options for your employees.</p>
                </CardContent>
              </Card>
            </Link>
          </div>
        </div>
      </section>

      {/* Carrier Carousel Component - Light Blue Background */}
      <div className="bg-blue-50">
        <CarrierCarousel />
      </div>

      {/* Benefits Section - Light Gray Background */}
      <section className="py-8 px-4 bg-gray-100">
        <div className="container mx-auto max-w-4xl text-center">
          <h2 className="text-3xl font-bold mb-4 text-gray-900">Benefits to working with a Former Fraud Specialist:</h2>
          <p className="text-xl text-gray-700 italic">
            "I used to investigate healthcare fraud monies, now I investigate health insurance savings for you!"
          </p>
        </div>
      </section>

      {/* Testimonials Section - Auto-Scrolling */}
      <section className="py-10 px-4 bg-white overflow-hidden">
        <div className="container mx-auto max-w-7xl">
          <h2 className="text-3xl font-bold text-center mb-3 text-gray-900">What Our Clients Say</h2>
          <div className="text-center mb-8">
            <div className="text-5xl font-bold text-teal-600">5.0</div>
            <div className="text-gray-600">Based on 234 positive reviews</div>
            <div className="text-sm text-gray-500">TrustScore: Excellent</div>
          </div>
          
          {/* Scrolling Testimonials Container */}
          <div className="relative">
            <div className="flex animate-scroll-testimonials gap-6">
              {/* First set of testimonials */}
              {testimonials.map((testimonial, index) => (
                <Card key={`first-${index}`} className="hover:shadow-lg transition-shadow flex-shrink-0 w-80">
                  <CardContent className="p-6">
                    <div className="flex mb-2">
                      {[...Array(testimonial.rating)].map((_, i) => (
                        <span key={i} className="text-yellow-400">★</span>
                      ))}
                    </div>
                    <p className="text-gray-700 mb-4 italic">"{testimonial.text}"</p>
                    <p className="font-semibold text-gray-900">{testimonial.name}</p>
                    <p className="text-sm text-gray-500">{testimonial.date}</p>
                  </CardContent>
                </Card>
              ))}
              {/* Duplicate set for seamless loop */}
              {testimonials.map((testimonial, index) => (
                <Card key={`second-${index}`} className="hover:shadow-lg transition-shadow flex-shrink-0 w-80">
                  <CardContent className="p-6">
                    <div className="flex mb-2">
                      {[...Array(testimonial.rating)].map((_, i) => (
                        <span key={i} className="text-yellow-400">★</span>
                      ))}
                    </div>
                    <p className="text-gray-700 mb-4 italic">"{testimonial.text}"</p>
                    <p className="font-semibold text-gray-900">{testimonial.name}</p>
                    <p className="text-sm text-gray-500">{testimonial.date}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Start Your Journey Section - Light Teal Background */}
      <section id="contact" className="py-10 px-4 bg-teal-50">
        <div className="container mx-auto max-w-4xl">
          <h2 className="text-3xl font-bold text-center mb-3 text-gray-900">Start Your Journey Today!</h2>
          <p className="text-center text-gray-600 mb-8 text-lg">The Process is Easy, 3 Simple Steps:</p>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
            <div className="text-center">
              <div className="w-16 h-16 bg-teal-600 text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">1</div>
              <h3 className="text-xl font-semibold mb-3 text-gray-900">Complete Confidential Survey</h3>
              <div className="h-12 flex items-center justify-center mb-3">
                {/* Empty space for alignment */}
              </div>
              <div className="space-y-2">
                <Button asChild className="w-full bg-teal-600 hover:bg-teal-700 text-white">
                  <a href="https://app.retireflo.com/paulette-lifeline-plans" target="_blank" rel="noopener noreferrer">
                    Medicare
                  </a>
                </Button>
                <Button asChild className="w-full bg-teal-600 hover:bg-teal-700 text-white">
                  <a href="https://app.retireflo.com/paulette-%7C-insurance-for-individuals-&-families" target="_blank" rel="noopener noreferrer">
                    Individual/Family
                  </a>
                </Button>
              </div>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-teal-600 text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">2</div>
              <h3 className="text-xl font-semibold mb-3 text-gray-900">48 Hour Research Period</h3>
              <div className="h-12 flex items-center justify-center mb-3">
                <p className="text-gray-600">CMS Regulation</p>
              </div>
              <div className="space-y-2">
                <Button asChild className="w-full bg-teal-600 hover:bg-teal-700 text-white">
                  <a href="tel:734.209.6712">
                    <Phone className="mr-2 inline" size={18} />
                    Call for Questions
                  </a>
                </Button>
              </div>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-teal-600 text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">3</div>
              <h3 className="text-xl font-semibold mb-3 text-gray-900">We Review Together</h3>
              <div className="h-12 flex items-center justify-center mb-3">
                {/* Empty space for alignment */}
              </div>
              <div className="space-y-2">
                <Button asChild className="w-full bg-teal-600 hover:bg-teal-700 text-white">
                  <a href="https://calendly.com/llplans/30min" target="_blank" rel="noopener noreferrer">
                    Schedule a Phone Appointment
                  </a>
                </Button>
              </div>
            </div>
          </div>

          <div className="text-center space-y-4">
            <Button asChild size="lg" className="bg-teal-600 hover:bg-teal-700 text-white text-xl px-8 py-6">
              <a href="tel:734.209.6712">
                <Phone className="mr-2" />
                Call: 734.209.6712
              </a>
            </Button>
            <div>
              <Button asChild variant="outline" className="border-teal-600 text-teal-600 hover:bg-teal-50">
                <a href="mailto:Paulette@llplans.com">
                  <Mail className="mr-2" />
                  Email: Paulette@llplans.com
                </a>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Form Component */}
      <ContactForm />
    </div>
  );
};

export default Home;
